import { useState } from 'react'
import { Box, ButtonBase } from '@mui/material'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import type { ReactNode } from 'react'
import { Sidebar } from './Sidebar'
import { colorTokens } from '../../../shared/styles/theme'
import { RADIUS } from '../../../shared/styles/radii'

const OPEN_KEY = 'mamoru.sidebar.open'

/** 開閉は端末に残す。localStorage が使えない環境でも開いた状態で成立させる。 */
const readIsOpen = () => {
  try {
    return window.localStorage.getItem(OPEN_KEY) !== 'false'
  } catch {
    return true
  }
}

const writeIsOpen = (isOpen: boolean) => {
  try {
    window.localStorage.setItem(OPEN_KEY, String(isOpen))
  } catch {
    // 保存できなくても開閉そのものは成立する
  }
}

interface Props {
  selectedId: string | null
  onSelect: (ocrRequestEventId: string) => void
  children: ReactNode
}

/**
 * app shell: Sidebar(一覧・224px・格納可能) + Main。
 *
 * **TopBar を置かない。** 面が詳細 1 つになり breadcrumb の指す先が無くなったので、バーごと引いた
 * (松田 8/29「KISS で引き算」・00-principles/03-subtraction.md §1)。格納中の復帰導線 `>>` は
 * Main の左上に置く(正典は TopBar が持つとしているが、その TopBar を引いたための逸脱。
 * 04-apps/mamoru.md 判断2 に登録)。
 */
export const Layout = ({ selectedId, onSelect, children }: Props) => {
  const [isOpen, setIsOpen] = useState(readIsOpen)

  const setOpen = (next: boolean) => {
    setIsOpen(next)
    writeIsOpen(next)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        background: colorTokens.bg.default,
      }}
    >
      {isOpen && (
        <Sidebar
          selectedId={selectedId}
          onSelect={onSelect}
          onCollapse={() => setOpen(false)}
        />
      )}
      <Box
        component="main"
        sx={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}
      >
        {!isOpen && (
          <ButtonBase
            aria-label="一覧を開く"
            onClick={() => setOpen(true)}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
              width: 24,
              height: 24,
              borderRadius: `${RADIUS.md}px`,
              color: colorTokens.fg.muted,
              background: colorTokens.bg.default,
              border: `1px solid ${colorTokens.border.default}`,
              boxShadow: colorTokens.shadow.cardSubtle,
            }}
          >
            <KeyboardDoubleArrowRightIcon sx={{ fontSize: 14 }} />
          </ButtonBase>
        )}
        {children}
      </Box>
    </Box>
  )
}
